import { useEffect, useState, useRef, useContext, useCallback } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  FaComments,
  FaImage,
  FaPaperPlane,
  FaSearch,
  FaTimes,
  FaEllipsisV,
  FaChevronLeft,
  FaUserCircle,
  FaRegSmile,
  FaSpinner
} from "react-icons/fa";
import OrderDetail from "./OrderDetail";
import { server, serverUrl } from "../../config/apiConfig";
import LoaderContext from "../../components/loader/LoaderContext";
import PreSetMessages from "../../apis/PreSuggestMessage.apis";
import EmojiPicker from "emoji-picker-react";
import useWindowSize from "../../components/useWindowSize";
import { chatApi } from "../../apis/Chat.api";
import { toast } from "react-toastify";

const socket = io(`${serverUrl}/chat-request`);

const BulkChat = () => {

  const [selectedChat, setSelectedChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [presetMessages, setPresetMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);


  const { currentUser } = useSelector((state) => state.store);
  const { handleLoading } = useContext(LoaderContext)
  const { width } = useWindowSize();
  const vendorId = currentUser?._id;
  const isMobile = width < 768;


  const chatsRef = useRef([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        100
      )}px`;
    }
  }, [message]);


  const getAllPreMessages = useCallback(async () => {
    handleLoading(true);
    try {
      const res = await PreSetMessages.getAllPreMessages();
      setPresetMessages(res?.data?.data || []);
    } catch (err) {
      console.error("Error fetching preset messages:", err);
    } finally {
      handleLoading(false);
    }
  }, []);

  const fetchChatUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${server}chat-users/${vendorId}`);
      setChats(res.data);
      chatsRef.current = res.data.map((chat) => chat._id.sender_id);
    } catch (error) {
      console.error("Error fetching chat users:", error);
    }
  }, [vendorId]);

  useEffect(() => {
    getAllPreMessages();
    fetchChatUsers();
  }, [getAllPreMessages, fetchChatUsers]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!vendorId) return;

    socket.emit("register", vendorId);

    socket.on("receiveMessage", (newMessage) => {
      const senderId = newMessage._id.sender_id;
      const productId = newMessage._id.product_id;

      setChats((prevChats) => {
        let chatIndex = prevChats.findIndex(
          (chat) => chat._id.sender_id === senderId && chat._id.product_id === productId
        );
        let updatedChats = [...prevChats];
        let chatObj;
        if (chatIndex !== -1) {
          chatObj = { ...updatedChats[chatIndex] };
          if (!(
            selectedChat &&
            senderId === selectedChat._id.sender_id &&
            productId === selectedChat._id.product_id
          )) {
            chatObj.unread_count = (chatObj.unread_count || 0) + 1;
          }
          chatObj.message = newMessage.message || chatObj.message;
          chatObj.last_message_time = newMessage.created_at || chatObj.last_message_time;
          updatedChats.splice(chatIndex, 1);
        } else {
          chatObj = {
            _id: { sender_id: senderId, product_id: productId },
            sender: newMessage.sender || {},
            product: newMessage.product || {},
            message: newMessage.message,
            last_message_time: newMessage.created_at,
            unread_count: 1,
          };
        }
        // Add to top
        return [chatObj, ...updatedChats];
      });

      // If currently selected chat, add message to messages
      if (
        selectedChat &&
        senderId === selectedChat._id.sender_id &&
        productId === selectedChat._id.product_id
      ) {
        setMessages((prev) => [...prev, newMessage]);
      }
    });

    socket.on("typing", ({ senderId, isTyping }) => {
      if (selectedChat && selectedChat._id.sender_id === senderId) {
        setIsTyping(isTyping);
      }
    });

    return () => {
      socket.off("receiveMessage");
      socket.off("typing");
    };
  }, [selectedChat, vendorId]);

  const fetchMessages = async (chat) => {
    setSelectedChat(chat);
    try {
      const res = await axios.get(
        `${server}messages/${vendorId}/${chat._id.sender_id}/${chat._id.product_id}`
      );
      setMessages(res.data);
      setChats((prevChats) =>
        prevChats.map((c) =>
          c._id.sender_id === chat._id.sender_id && c._id.product_id === chat._id.product_id
            ? { ...c, unread_count: 0 }
            : c
        )
      );
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() && files.length === 0) return;
    let imageUrl = null;
    if (files.length > 0) {
      setIsUploading(true);
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      formData.append("receiverId", selectedChat._id.sender_id);
      formData.append("senderId", vendorId);
      try {
        const response = await chatApi.uploadFiles(formData, { headers: { "Content-Type": "multipart/form-data" } });
        imageUrl = response.data.filePaths;

        imageUrl.forEach((fileData) => {
          const data = {
            sender_id: vendorId,
            receiver_id: selectedChat._id.sender_id,
            product_id: selectedChat._id.product_id,
            file: fileData,
          };

          socket.emit("sendMessage", data);
          setMessages((prev) => [...prev, data]);
          setMessage("");
          setPreview([]);
        });
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error(error.response?.data?.message);
        return;
      } finally {
        setIsUploading(false);
        setFiles([]);
        setPreview([]);
      }
    }
    if (message.trim()) {
      const newMessage = {
        sender_id: vendorId,
        receiver_id: selectedChat._id.sender_id,
        product_id: selectedChat._id.product_id,
        message,
      };

      socket.emit("sendMessage", newMessage);
      setMessages((prev) => [...prev, newMessage]);
      setMessage("");
    }
  };


  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    setFiles(selectedFiles);

    const newPreviews = selectedFiles.map(file => ({
      url: URL.createObjectURL(file),
      name: file.name
    }));

    setPreview(newPreviews);

    e.target.value = '';
  };

  const removePreview = (index) => {
    URL.revokeObjectURL(preview[index].url);

    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreview(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    return () => {
      preview.forEach(item => URL.revokeObjectURL(item.url));
    };
  }, [preview]);


  const onEmojiClick = useCallback((emojiData) => {
    setMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  }, []);

  useEffect(() => {
    if (!selectedChat || !message) return;

    const timer = setTimeout(() => {
      socket.emit("typing", {
        senderId: vendorId,
        receiverId: selectedChat._id.sender_id,
        isTyping: !!message
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [message, selectedChat, vendorId]);

  const filteredChats = chats.filter((chat) =>
    chat?.sender?.first_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100 overflow-hidden">
      {isMobile && (
        <div className="flex items-center justify-between p-3 bg-[--color--1] text-white shadow-md">
          {!showSidebar ? (
            <button
              onClick={() => {
                setShowSidebar(true);
                setShowOrderDetail(false);
              }}
              className="p-2"
              aria-label="Back to chats"
            >
              <FaChevronLeft size={18} />
            </button>
          ) : (
            <h1 className="font-bold text-lg">Customer Inquiries</h1>
          )}

          {!showSidebar && selectedChat && (
            <button
              onClick={() => setShowOrderDetail(!showOrderDetail)}
              className="p-2"
              aria-label="Toggle order details"
            >
              <FaEllipsisV size={18} />
            </button>
          )}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden relative border-b-2 border-gray-200">
        {/* Sidebar - Chat List */}
        <aside
          className={`${showSidebar ? 'flex' : 'hidden'} ${isMobile ? 'absolute inset-0 z-10 bg-white' : 'flex'} md:flex flex-col md:w-1/3 lg:w-1/4 bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out`}
        >
          <div className="p-3 md:p-4 bg-[--color--1] text-white shadow-sm">
            <h2 className="font-bold text-lg md:text-xl flex items-center gap-2">
              <FaComments className="flex-shrink-0" size={20} />
              <span className="truncate">Customer Chats</span>
            </h2>
          </div>

          <div className="p-2 md:p-3 bg-white border-b border-gray-200">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search customers..."
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--color--1] text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search chats"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredChats.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
                <FaComments size={40} className="mb-3 opacity-50" />
                <p className="text-sm">No conversations found</p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="mt-2 text-sm text-[--color--1] hover:underline"
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredChats.map((chat) => (
                  <li
                    key={`${chat._id.sender_id}-${chat._id.product_id}`}
                    className={`transition-colors duration-150 ${selectedChat?._id?.sender_id === chat._id.sender_id &&
                      selectedChat?._id?.product_id === chat._id.product_id
                      ? "bg-[--color--1] bg-opacity-10 border-l-4 border-[--color--1]"
                      : chat.unread_count > 0
                        ? "bg-yellow-50 border-l-4 border-yellow-400"
                        : "hover:bg-gray-50"
                      }`}
                  >
                    <button
                      onClick={() => {
                        fetchMessages(chat);
                        if (isMobile) {
                          setShowSidebar(false);
                          setShowOrderDetail(false);
                        }
                      }}
                      className="w-full flex items-center p-2 md:p-3 focus:outline-none"
                      aria-label={`Chat with ${chat.sender.first_name}`}
                    >
                      <div className="flex-shrink-0 mr-2 md:mr-3 relative">
                        {chat?.sender?.profile_pic ? (
                          <img
                            src={chat.sender.profile_pic}
                            alt={chat.sender.first_name}
                            className="w-10 h-10 rounded-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <FaUserCircle className="text-gray-400 text-3xl" />
                        )}
                        {chat.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 bg-[--color--1] text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                            {chat.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex justify-between items-center">
                          <p className={`font-medium text-sm md:text-base truncate ${selectedChat?._id?.sender_id === chat._id.sender_id &&
                            selectedChat?._id?.product_id === chat._id.product_id
                            ? "text-white font-bold"
                            : "text-gray-800"
                            }`}>
                            {chat?.sender?.first_name} {chat?.sender?.last_name}
                          </p>
                          <time className="text-xs text-gray-500 whitespace-nowrap ml-2">
                            {formatTime(chat.last_message_time)}
                          </time>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className={`text-xs md:text-sm truncate ${selectedChat?._id?.sender_id === chat._id.sender_id &&
                            selectedChat?._id?.product_id === chat._id.product_id
                            ? "text-white"
                            : "text-gray-500"
                            }`}>
                            {chat.message || "No new messages yet..."}
                          </p>
                          {chat.product?.images?.[0] && (
                            <img
                              src={chat.product.images[0]}
                              alt={chat.product.name}
                              className="ml-2 w-6 h-6 rounded object-cover"
                              loading="lazy"
                            />
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Main Chat Area */}
        <main
          className={`${(!showSidebar || !isMobile) ? 'flex' : 'hidden'} flex-1 flex-col bg-gray-50 relative`}
        >
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <header className="flex items-center justify-between p-2 md:p-3 bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
                <div className="flex items-center">
                  {isMobile && (
                    <button
                      onClick={() => {
                        setShowSidebar(true);
                        setShowOrderDetail(false);
                      }}
                      className="mr-2 p-1 text-gray-600"
                      aria-label="Back to chats"
                    >
                      <FaChevronLeft size={18} />
                    </button>
                  )}
                  <div className="flex-shrink-0 mr-2 md:mr-3">
                    {selectedChat?.sender?.profile_pic ? (
                      <img
                        src={selectedChat.sender.profile_pic}
                        alt={selectedChat.sender.first_name}
                        className="w-10 h-10 rounded-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <FaUserCircle className="text-gray-400 text-3xl" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <h2 className="font-semibold text-sm md:text-base truncate">
                        {selectedChat?.sender?.first_name} {selectedChat?.sender?.last_name}
                      </h2>
                      {isTyping && (
                        <span className="ml-2 text-xs text-gray-500 italic">
                          typing...
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {selectedChat?.product?.name}
                    </p>
                  </div>
                </div>
                {isMobile && (
                  <button
                    onClick={() => setShowOrderDetail(!showOrderDetail)}
                    className="p-1 text-gray-600"
                    aria-label="Toggle order details"
                  >
                    <FaEllipsisV size={18} />
                  </button>
                )}
              </header>

              {/* Messages Area */}
              <div
                className="flex-1 p-2 md:p-4 overflow-y-auto bg-[#e5ddd5] bg-opacity-30"
                style={{
                  backgroundImage: "url('https://web.whatsapp.com/img/bg-chat-tile-light_a4be512e7195b6b733d9110b408f075d.png')",
                  backgroundSize: '250px'
                }}
              >
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <FaComments size={48} className="mb-3 opacity-50" />
                    <p className="text-sm md:text-base">No messages yet</p>
                    <p className="text-xs md:text-sm">Start the conversation</p>
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex mb-3 ${msg.sender_id === vendorId ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] md:max-w-md rounded-lg p-2 md:p-3 ${msg.sender_id === vendorId
                          ? "bg-[--color--1] text-white rounded-tr-none"
                          : "bg-white text-gray-800 rounded-tl-none shadow"
                          }`}
                      >
                        {msg.file && (
                          <a href={msg.file} target="_blank" rel="noopener noreferrer">
                            <img
                              src={msg.file}
                              alt="Sent file"
                              className="w-full max-h-48 md:max-h-64 rounded mb-1 md:mb-2 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                              loading="lazy"
                            />
                          </a>
                        )}
                        <p className="whitespace-pre-wrap text-sm md:text-base">{msg.message}</p>
                        <div className={`text-xs mt-1 text-right ${msg.sender_id === vendorId ? "text-white opacity-80" : "text-gray-500"
                          }`}>
                          {formatTime(msg.created_at)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Preview Images */}
              {preview.length > 0 && (
                <div className="px-2 py-1 md:px-4 md:py-2 bg-gray-100 border-t border-gray-200 flex gap-2 overflow-x-auto">
                  {preview.map((previewItem, index) => (
                    <div key={index} className="relative flex-shrink-0">
                      <img
                        src={previewItem.url}
                        alt={`Preview ${index}`}
                        className="w-14 h-14 md:w-16 md:h-16 rounded-md object-cover border border-gray-200"
                      />
                      <button
                        onClick={() => removePreview(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow hover:bg-red-600 transition-colors"
                        aria-label="Remove image"
                      >
                        <FaTimes size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Message Input */}
              <div className="p-2 md:p-3 bg-white border-t border-gray-200 sticky bottom-0">
                <div className="flex items-end gap-1 md:gap-2 relative">
                  <div className="flex items-center">
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="text-[--color--1] hover:text-green-700 p-2 rounded-full transition-colors"
                      aria-label="Attach file"
                    >
                      <FaImage size={18} />
                    </button>
                    <button
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="text-gray-500 hover:text-gray-700 p-2 rounded-full transition-colors"
                      aria-label="Add emoji"
                    >
                      <FaRegSmile size={18} />
                    </button>
                  </div>

                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={isUploading}
                    placeholder="Type a message..."
                    className="flex-1 border border-gray-300 rounded-full md:rounded-md py-2 px-4 focus:outline-none focus:ring-2 focus:ring-[--color--1] resize-none overflow-y-auto transition-all text-sm md:text-base"
                    style={{
                      minHeight: '40px',
                      maxHeight: '100px',
                      lineHeight: '1.4',
                    }}
                    rows={1}
                    aria-label="Message input"
                  />

                  <button
                    onClick={sendMessage}
                    disabled={(!message.trim() && files.length === 0) || isUploading}
                    className={`p-2 rounded-full transition-colors ${(message.trim() || files.length > 0) && !isUploading
                      ? "bg-[--color--1] text-white"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    aria-label="Send message"
                  >
                    {isUploading ? (
                      <p>loading...</p>
                    ) : (
                      <FaPaperPlane size={18} />
                    )}
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={handleFileChange}
                    multiple
                  />

                  {showEmojiPicker && (
                    <div
                      ref={emojiPickerRef}
                      className="absolute bottom-12 left-10 z-20"
                    >
                      <EmojiPicker
                        onEmojiClick={onEmojiClick}
                        width={300}
                        height={350}
                        searchDisabled
                        skinTonesDisabled
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 p-4 md:p-6">
              <div className="text-center max-w-xs md:max-w-md">
                <FaComments size={48} className="mx-auto mb-3 text-[--color--1] opacity-50" />
                <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-2">No chat selected</h3>
                <p className="text-sm md:text-base text-gray-500">
                  {isMobile ? 'Tap on a conversation to start chatting' : 'Select a conversation to start chatting'}
                </p>
                {isMobile && (
                  <button
                    onClick={() => setShowSidebar(true)}
                    className="mt-4 px-4 py-2 bg-[--color--1] text-white rounded-lg text-sm"
                  >
                    View all conversations
                  </button>
                )}
              </div>
            </div>
          )}
        </main>

        {/* Order Details Panel */}
        {selectedChat && (
          <aside
            className={`${showOrderDetail ? 'flex' : 'hidden'} ${isMobile ? 'absolute inset-0 z-10 bg-white' : 'flex'} flex-col md:w-1/3  bg-white border-l border-gray-200 shadow-lg`}
          >
            <div className="p-3 md:p-4 bg-[--color--1] text-white flex justify-between items-center">
              <h3 className="font-semibold text-sm md:text-base">Order Details</h3>
              <button
                onClick={() => setShowOrderDetail(false)}
                className="p-1"
                aria-label="Close order details"
              >
                <FaTimes size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <OrderDetail
                data={selectedChat?.product}
                presetMessages={presetMessages}
                onPresetClick={(msg) => {
                  setMessage(msg);
                  setShowOrderDetail(false);
                  textareaRef.current?.focus();
                }}
              />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default BulkChat;